import React, { useState, useEffect, useMemo } from "react";
import { Skeleton } from "antd";
import { useNavigate } from "react-router-dom";
import { getIPBillDetailsApi } from "../../../../services/receptionist.services";
import useQuery from "../../../../hooks/useQuery";
import { updateUrlSearchParams } from "../../../../helpers/urlFilters";
import dayjs from "dayjs";
import RecordPayment from "./RecordPayment";
import ServicesTableIp from "./ServicesTableIp";
import BedsIp from "./BedsIp";
import BillingSummaryCards from "./BillingSummaryCards";
import TotalAmountPaid from "./TotalAmountPaid";
import PaymentHistoryIp from "./PaymentHistoryIp";
import styles from "./BillingIp.module.css";

const dateFormat = "YYYY-MM-DD";

const BillingIp = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const activeTab = params?.billing_view || "detailed";
  const dateFilter = useMemo(() => {
    if (!params?.start_date || !params?.end_date) return null;
    return {
      start_date: dayjs(params.start_date),
      end_date: dayjs(params.end_date),
    };
  }, [params?.start_date, params?.end_date]);

  const [billData, setBillData] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);

  const setActiveTab = (tab) => {
    updateUrlSearchParams(navigate, {
      billing_view: tab,
      start_date: tab === "summary" ? null : params?.start_date || null,
      end_date: tab === "summary" ? null : params?.end_date || null,
    });
  };

  const setDateFilter = (filter) => {
    if (!filter) {
      updateUrlSearchParams(navigate, { start_date: null, end_date: null });
      return;
    }
    updateUrlSearchParams(navigate, {
      start_date: filter.start_date.format(dateFormat),
      end_date: filter.end_date.format(dateFormat),
    });
  };

  const refreshData = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    setBillData(null);
    setLoading(true);

    getIPBillDetailsApi(
      params?.patient,
      dateFilter
        ? `start_date=${dateFilter.start_date.format(
            dateFormat
          )}&end_date=${dateFilter.end_date.format(dateFormat)}`
        : ""
    )
      .then((res) => {
        setBillData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [params?.patient, refresh, dateFilter]);

  const cardData = billData?.data;
  const paymentDetails = cardData?.payment_details;

  return (
    <div className={styles.page}>
      {loading ? (
        <Skeleton active />
      ) : (
        <div className={styles.columns}>
          <div className={styles.leftColumn}>
            <BillingSummaryCards cardData={cardData} />
            <BedsIp billData={billData} />
            <ServicesTableIp
              key={params?.patient}
              billData={billData}
              setDateFilter={setDateFilter}
              dateFilter={dateFilter}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </div>

          <aside className={styles.rightColumn}>
            <RecordPayment billData={billData} refreshData={refreshData} />
            <TotalAmountPaid paymentData={paymentDetails} />
            <PaymentHistoryIp
              paymentData={paymentDetails}
              bill_id={cardData?.bill?.id}
              refreshData={refreshData}
              activePatient={cardData?.admission?.is_active}
            />
          </aside>
        </div>
      )}
    </div>
  );
};

export default BillingIp;
